import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import RoundedButton from 'Components/ComponentLibrary/Atoms/Inputs/RoundedButton';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { useSnackbarContext } from 'Context/SnackbarContext';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import WizardStepDeleteButton
  from 'Views/Page/Content/Templates/WizardTemplates/WizardStepTemplate/Content/WizardStepDeleteButton';
import CenteredActions from 'Views/Page/Content/Modal/Components/CenteredActions';
import PageModalTitle from 'Views/Page/Content/Modal/Components/PageModalTitle';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import { getLightBorder } from 'Components/utils';
import { dispatchBalanceSheetSidebarUpdated } from 'Hooks/useRefreshEvent';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  chooseCategory: {
    marginBottom: theme.spacing(2),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  classificationSection: {
    margin: theme.spacing(2, 0, 0),
    padding: theme.spacing(2, 0, 0),
    width: '100%',
    ...getLightBorder(theme, { top: true }),
  },
  timingSection: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(3, 0, 0),
    width: '100%',
    ...getLightBorder(theme, { top: true }),
  },
});

const FacetPlanAddBalanceTransfer = ({
  className,
  classes,
  onSubmit,
}) => {
  const { saving, resource, handleChange } = useMetaLayoutContext();
  const { clearModalCollection } = useModalContext();
  const { displayErrorMessage, displaySuccess } = useSnackbarContext();
  const { refreshPageContentCollection } = usePageContentContext();

  const isEditing = Boolean(resource.id() !== '0');
  const handleOnSubmit = async () => {
    try {
      const res = await resource.submit();

      displaySuccess(`Successfully ${isEditing ? 'Modified' : 'Added'} Balance Transfer`);

      // Refresh
      dispatchBalanceSheetSidebarUpdated();
      onSubmit(res);
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const handleDelete = async () => {
    try {
      if (isEditing && resource.meta().action('DELETE')) {
        await resource.delete();

        // Close modal / display Success!
        clearModalCollection();
        dispatchBalanceSheetSidebarUpdated();
        displaySuccess('Successfully Removed Balance Transfer!');
      }
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  return (
    <Box className={classNames(classes.root, className)}>
      <PageModalTitle>
        <Title value={resource.attr('title')} />
      </PageModalTitle>

      <MetaField
        attr="eventType"
        onChange={(type) => {
          // Default rollovers to full balance
          if (type.includes('virtualAccountRollover')) {
            handleChange('isFullBalance', 'true');
          } else {
            handleChange('isFullBalance', 'false');
          }
        }}
      />
      <MetaField attr="from" />
      <MetaField attr="to" />
      <MetaField attr="value" />
      <Box className={classes.classificationSection}>
        <MetaField
          attr="isFullBalance"
          onChange={(type) => {
            // If IsFullBalance:
            if (type.includes('true')) {
              handleChange('frequency', 'one-time');
            }
          }}
        />
        <MetaField attr="affectedByInflation" />
        <MetaField attr="isQualifiedWithdrawal" />
      </Box>
      <Box className={classes.timingSection}>
        <MetaField attr="frequency" />
        <MetaField attr="startType" />
        <MetaField attr={'startDate'} />
        <MetaField attr={'startAtAge'} />
        <MetaField attr={'endType'} />
        <MetaField attr={'endDate'} />
        <MetaField attr={'endAtAge'} />

      </Box>
      {isEditing && resource.meta().action('DELETE') ? (
        <WizardStepDeleteButton
          className={classes.deleteButton}
          onDelete={handleDelete}
          onDeleteSuccess={() => refreshPageContentCollection()}
          dialogProps={{
            title: 'Are you sure?',
            message:
              'Removing this item will delete all of its information. This cannot be undone.',
            cancelLabel: 'Nevermind',
            confirmLabel: 'Remove',
          }}
        />
      ) : null}
      <CenteredActions>
        <RoundedButton
          isLoading={saving}
          onClick={handleOnSubmit}
          fullWidth
          test={'facet-plan-add-balance-transfer-button'}
        >
          {`${isEditing ? 'Save' : 'Add'}`}
        </RoundedButton>
      </CenteredActions>
    </Box>
  );
};

FacetPlanAddBalanceTransfer.defaultProps = {
  className: '',
  onSubmit: () => {},
};

FacetPlanAddBalanceTransfer.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
};

export default withStyles(styles)(FacetPlanAddBalanceTransfer);
